import React, { useState, useEffect, useCallback } from 'react';
import {
  StyleSheet,
  View,
  Text,
  ScrollView,
  Platform,
  Image,
  ActivityIndicator,
  TouchableOpacity,
  Alert,
} from 'react-native';
import { 
  generateAndStorePDF, 
  generateAndDownloadPDF, 
  checkGotenbergAvailability 
} from '../../services/pdfGenerator';
import Svg, { Circle, Text as SvgText } from 'react-native-svg';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { SignatureDisplay } from '../ui/SignatureCanvas';
import { Vehicle, Driver } from '../../types';
import { 
  getVehicleDeliveryFormByAssignmentHybrid,
  getVehicleDamagesByDeliveryFormHybrid 
} from '../../api/supabase';
import { supabase } from '../../api/supabase';
import theme from '../../theme/index';

const LOGO_ASSET = require('../../../assets/logo-icon-only.svg');
const VEHICLE_DIAGRAM_ASSET = require('../../assets/tesla-model-s.jpeg');

const isWeb = Platform.OS === 'web';

// Helper function to convert image URL to base64
const imageUrlToBase64 = async (url: string): Promise<string> => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to convert image to base64:', error);
    return '';
  }
};

// Helper function to resolve asset URIs for web
const resolveAssetUri = (asset: any): string => {
  if (isWeb && asset && typeof asset === 'object' && 'default' in asset) {
    return asset.default;
  }
  if (typeof asset === 'string') {
    return asset;
  }
  return '';
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatDateForPrint = (dateString?: string | null) => {
  if (!dateString) return 'Not set';
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return escapeHtml(String(dateString));
    }
    return escapeHtml(date.toLocaleDateString());
  } catch {
    return escapeHtml(String(dateString));
  }
};

const severityColorMap: Record<string, string> = {
  major: '#ff4444',
  moderate: '#ffaa00',
  minor: '#44aa44',
};

const buildDamageMarkersSvg = (damageMarkers: DamageMarker[]) => {
  if (!damageMarkers.length) {
    return '';
  }

  const circles = damageMarkers
    .map((marker, i) => {
      const color = severityColorMap[marker.severity] || '#44aa44'; // Use severity colors
      // Use the same coordinate system as screen form (viewBox 0 0 350 250)
      return `<circle cx="${marker.x}" cy="${marker.y}" r="8" fill="${color}" stroke="#000" stroke-width="2" opacity="0.95" />
        <text x="${marker.x}" y="${marker.y + 2}" text-anchor="middle" dominant-baseline="middle" fill="#fff" font-size="10" font-weight="700">${i + 1}</text>`;
    })
    .join('\n        ');

  // Use the same viewBox as screen form (350x250)
  return `<svg viewBox="0 0 350 250" preserveAspectRatio="xMidYMid meet" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 10;">
      ${circles}
    </svg>`;
};

const buildDamageListHtml = (damageMarkers: DamageMarker[]) => {
  if (!damageMarkers.length) {
    return '<div class="damage-empty">لا توجد أضرار مسجلة / No damages recorded</div>';
  }

  return damageMarkers
    .map((marker, i) => {
      const color = severityColorMap[marker.severity] || '#44aa44'; // Use severity colors
      const typeLabel = marker.type ? escapeHtml(marker.type) : 'Unknown';
      const severityLabel = marker.severity ? escapeHtml(marker.severity) : 'minor';
      const description = marker.description ? escapeHtml(marker.description) : 'No description';

      return `<div class="damage-item">
        <div class="damage-index" style="background: ${color};">${i + 1}</div>
        <div class="damage-info">
          <div class="damage-title">${typeLabel} (${severityLabel})</div>
          <div class="damage-description">${description}</div>
        </div>
      </div>`;
    })
    .join('\n      ');
};

interface DamageMarker {
  id: string;
  x: number;
  y: number;
  type: 'scratch' | 'dent' | 'missing' | 'broken';
  severity: 'minor' | 'moderate' | 'major';
  description?: string;
  photoUrl?: string; // Photo URL from Supabase Storage
  timestamp: string;
}



interface DeliveryHistoryDisplayProps {
  assignmentId: string;
  vehicle?: Vehicle;
  driver?: Driver;
  suffix?: 'A' | 'B'; // For partnership assignments: A = primary driver, B = secondary driver
}

interface PrintTemplateParams {
  deliveryForm: any;
  vehicle: any;
  driver: any;
  damageMarkers: DamageMarker[];
  isPartnership: boolean;
  assetUris: {
    logo: string;
    vehicleDiagram: string;
  };
}

const buildDeliveryPrintHtml = ({
  deliveryForm,
  vehicle,
  driver,
  damageMarkers,
  isPartnership,
  assetUris,
}: PrintTemplateParams) => {
  const logoUri = assetUris.logo || '';
  const diagramUri = assetUris.vehicleDiagram || '';

  const valueOrFallback = (value: any, fallback = 'N/A') =>
    value !== undefined && value !== null && value !== '' ? escapeHtml(String(value)) : fallback;

  const deliveryNotes = valueOrFallback(
    deliveryForm?.delivery_notes || deliveryForm?.additional_notes,
    'لا توجد ملاحظات / No notes'
  ).replace(/\n/g, '<br/>');

  const driverSignature = deliveryForm?.driver_signature || deliveryForm?.primary_driver_signature || '';
  const secondaryDriverSignature = deliveryForm?.secondary_driver_signature || '';
  const adminSignature = deliveryForm?.admin_signature || deliveryForm?.manager_signature || deliveryForm?.company_signature || '';

  const damageMarkersSvg = buildDamageMarkersSvg(damageMarkers);
  const damageList = buildDamageListHtml(damageMarkers);

  const companyNameAr = 'الشارقة لحلول النقل';
  const companyNameEn = 'Sharjah Transport Solutions';

  const deliveryDate = formatDateForPrint(deliveryForm?.delivery_date || deliveryForm?.assignment_date);
  const deliveryTime = valueOrFallback(
    deliveryForm?.delivery_time || deliveryForm?.assignment_time,
    'N/A'
  );
  const fuelQuantity = valueOrFallback(
    deliveryForm?.fuel_quantity || (deliveryForm?.fuel_level ? `${deliveryForm?.fuel_level}%` : ''),
    'N/A'
  );
  const vehicleNumber = valueOrFallback(
    deliveryForm?.delivery_vehicle_number || vehicle?.licensePlate || deliveryForm?.vehicle_license_plate,
    'N/A'
  );
  const makeModelYear = (() => {
    if (deliveryForm?.delivery_make_model_year) return escapeHtml(String(deliveryForm.delivery_make_model_year));
    if (vehicle?.makeModel && vehicle?.year) return escapeHtml(`${vehicle.makeModel} (${vehicle.year})`);
    if (vehicle?.makeModel) return escapeHtml(vehicle.makeModel);
    if (deliveryForm?.vehicle_make_model) return escapeHtml(String(deliveryForm.vehicle_make_model));
    return 'N/A';
  })();
  const mileage = valueOrFallback(
    deliveryForm?.delivery_mileage || deliveryForm?.initial_mileage,
    'N/A'
  );

  const driverName = (() => {
    if (driver?.name) return escapeHtml(driver.name);
    if (deliveryForm?.driver_name) return escapeHtml(String(deliveryForm.driver_name));
    if (deliveryForm?.primary_driver_name) return escapeHtml(String(deliveryForm.primary_driver_name));
    return 'Not available';
  })();
  
  const driverPhone = (() => {
    if (driver?.phone) return escapeHtml(driver.phone);
    if (deliveryForm?.driver_phone) return escapeHtml(String(deliveryForm.driver_phone));
    if (deliveryForm?.primary_driver_phone) return escapeHtml(String(deliveryForm.primary_driver_phone));
    return 'Not available';
  })();

  const conditionValue = (primary?: string, fallback?: string) =>
    valueOrFallback(primary || fallback, 'جيد / Good');

  return `<!doctype html>
    <html lang="en">
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <title>Vehicle Delivery Form</title>
        <style>
          @page { size: A4; margin: 10mm; }
          * { box-sizing: border-box; }
          html, body { margin: 0; padding: 0; font-family: 'Segoe UI', Arial, sans-serif; background: #fff; color: #1f2933; }
          body { padding: 0; }
          .sheet { max-width: 100%; margin: 0 auto; background: #fff; border: none; box-shadow: none; border-radius: 0; overflow: hidden; }
          .header { display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; border-bottom: 2px solid #000; background: #fff; }
          .header-left { display: flex; align-items: center; gap: 12px; flex: 1; }
          .logo { width: 50px; height: 50px; border-radius: 50%; border: 2px solid #000; display: flex; align-items: center; justify-content: center; background: #fff; }
          .logo img { width: 38px; height: 38px; object-fit: contain; }
          .company-info { display: flex; flex-direction: column; gap: 2px; }
          .company-info .ar { font-size: 15px; font-weight: 700; color: #0f172a; }
          .company-info .en { font-size: 11px; color: #475569; }
          .header-type { text-align: center; flex: 1; }
          .header-type .ar { font-size: 18px; font-weight: 700; color: #000; }
          .header-type .en { font-size: 12px; color: #666; margin-top: 2px; letter-spacing: 0.3px; }
          .section { padding: 12px 16px; border-bottom: 1px solid #ddd; }
          .section:last-of-type { border-bottom: none; }
          .section-title { text-align: center; font-size: 15px; font-weight: 700; color: #000; margin-bottom: 10px; position: relative; }
          .section-title::after { content: ''; width: 60px; height: 2px; background: #000; display: block; margin: 6px auto 0; border-radius: 999px; }
          .info-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; }
          .info-item { display: flex; flex-direction: column; gap: 4px; }
          .info-label { font-size: 10px; font-weight: 600; color: #666; text-transform: uppercase; letter-spacing: 0.4px; text-align: right; }
          .info-value { padding: 8px 10px; background: #f5f5f5; border: 1px solid #ddd; border-radius: 6px; min-height: 38px; text-align: right; font-size: 12px; color: #000; font-weight: 500; }
          .delivery-layout { display: grid; grid-template-columns: 1.1fr 0.9fr; gap: 16px; }
          .diagram-card { background: #f5f5f5; border: 1px solid #ddd; border-radius: 12px; padding: 10px 12px; }
          .diagram-title { text-align: center; font-size: 13px; font-weight: 600; color: #000; margin-bottom: 8px; }
          .diagram-image { position: relative; border-radius: 8px; overflow: hidden; border: 1px solid #ccc; background: #fff; padding: 8px; display: flex; justify-content: center; align-items: center; min-height: 220px; }
          .diagram-image img { width: 100%; max-width: 300px; height: auto; display: block; position: relative; z-index: 1; }
          .diagram-image svg { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; width: calc(100% - 16px); height: calc(100% - 16px); max-width: 300px; margin: auto; pointer-events: none; z-index: 2; }
          .diagram-meta { margin-top: 6px; font-size: 10px; color: #64748b; text-align: center; font-style: italic; }
          .delivery-data-column { display: flex; flex-direction: column; gap: 10px; }
          .data-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
          .data-field { display: flex; flex-direction: column; gap: 4px; }
          .field-label { font-size: 10px; font-weight: 600; color: #666; text-align: right; }
          .field-value { padding: 7px 9px; background: #f5f5f5; border: 1px solid #ddd; border-radius: 6px; text-align: right; font-size: 11px; color: #000; min-height: 34px; display: flex; align-items: center; justify-content: flex-end; }
          .conditions-inline { margin-top: 6px; display: flex; flex-direction: column; gap: 6px; }
          .condition-row-inline { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
          .condition-inline { display: flex; flex-direction: column; gap: 3px; }
          .condition-label-inline { font-size: 9px; font-weight: 600; color: #666; text-align: right; }
          .condition-value-inline { padding: 6px 8px; background: #fff; border: 1px solid #ccc; border-radius: 5px; text-align: center; font-size: 10px; font-weight: 600; color: #000; }
          .damage-list { margin-top: 10px; display: flex; flex-direction: column; gap: 6px; }
          .damage-item { display: flex; gap: 8px; padding: 8px; background: #fff; border: 1px solid #ccc; border-radius: 8px; align-items: flex-start; }
          .damage-index { width: 22px; height: 22px; border-radius: 50%; color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 11px; flex-shrink: 0; }
          .damage-info { flex: 1; }
          .damage-title { font-size: 11px; font-weight: 600; color: #000; }
          .damage-description { font-size: 10px; color: #666; margin-top: 2px; line-height: 1.3; }
          .damage-coordinates { font-size: 9px; color: #999; margin-top: 2px; font-family: monospace; }
          .damage-empty { padding: 10px; border-radius: 8px; border: 1px dashed #ccc; background: #f5f5f5; text-align: center; font-size: 11px; color: #666; }
          .notes-card { margin-top: 10px; background: #fff; border-radius: 8px; border: 1px solid #ddd; padding: 10px; }
          .notes-title { text-align: center; font-weight: 600; font-size: 11px; color: #000; margin-bottom: 8px; }
          .notes-body { font-size: 11px; line-height: 1.5; color: #000; text-align: right; min-height: 30px; }
          .conditions-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 12px; }
          .condition-item { background: #f8fafc; border-radius: 12px; padding: 12px 16px; border: 1px solid rgba(148, 163, 184, 0.3); display: flex; flex-direction: column; gap: 6px; }
          .condition-label { font-size: 12px; font-weight: 600; color: #475569; text-align: right; }
          .condition-value { text-align: center; font-size: 13px; font-weight: 600; color: #0f172a; background: #fff; border-radius: 8px; border: 1px solid rgba(148, 163, 184, 0.5); padding: 10px; }
          .signature-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; page-break-inside: avoid; }
          .signature-card { border: 1px solid #ddd; border-radius: 14px; overflow: hidden; background: #fff; }
          .signature-heading { padding: 10px; background: #000; color: #fff; font-weight: 600; text-align: center; font-size: 12px; }
          .signature-body { padding: 12px; min-height: 80px; display: flex; align-items: center; justify-content: center; }
          .signature-placeholder { font-size: 12px; color: #999; text-align: center; }
          .signature-image { max-width: 100%; max-height: 120px; object-fit: contain; }
          .footer-note { padding: 6px 12px; font-size: 8px; color: #999; text-align: center; background: #f5f5f5; border-top: 1px solid #ddd; page-break-inside: avoid; page-break-before: avoid; margin-top: -2px; }
          .print-actions { position: fixed; top: 16px; right: 16px; display: flex; gap: 8px; z-index: 1000; }
          .print-actions button { border: none; border-radius: 8px; padding: 10px 16px; font-weight: 600; cursor: pointer; box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12); display: flex; align-items: center; gap: 6px; }
          .print-btn { background: #22c55e; color: #fff; }
          .close-btn { background: #ef4444; color: #fff; }
          @media print {
            body { padding: 0; background: #fff; }
            .sheet { box-shadow: none; border: none; margin: 0; border-radius: 0; }
            .print-actions { display: none; }
          }
        </style>
      </head>
      <body>
        <div class="print-actions">
          <button class="print-btn" onclick="window.print()">Print</button>
          <button class="close-btn" onclick="window.close()">Close</button>
        </div>
        
        <div class="sheet">
          <div class="header">
            <div class="header-left">
              <div class="logo">
                <img src="${logoUri}" alt="Logo" />
              </div>
              <div class="company-info">
                <div class="ar">${companyNameAr}</div>
                <div class="en">${companyNameEn}</div>
              </div>
            </div>
            <div class="header-type">
              <div class="ar">استمارة تسليم مركبة</div>
              <div class="en">VEHICLE DELIVERY FORM</div>
            </div>
          </div>

          <div class="section">
            <div class="info-grid" style="grid-template-columns: repeat(4, 1fr);">
              <div class="info-item">
                <div class="info-label">اسم الشركة / Company Name</div>
                <div class="info-value">${companyNameAr} / ${companyNameEn}</div>
              </div>
              <div class="info-item">
                <div class="info-label">رقم اللوحة / License Plate</div>
                <div class="info-value">${vehicleNumber}</div>
              </div>
              <div class="info-item">
                <div class="info-label">اسم السائق / Driver Name</div>
                <div class="info-value">${driverName}</div>
              </div>
              <div class="info-item">
                <div class="info-label">رقم الهاتف / Phone Number</div>
                <div class="info-value">${driverPhone}</div>
              </div>
            </div>
          </div>

          <div class="section">
            <div class="section">
            <div class="section-title">بيانات تسليم المركبة / Vehicle Delivery Data</div>
            <div class="delivery-layout">
              <!-- LEFT COLUMN: Vehicle Diagram + Notes -->
              <div class="diagram-card">
                <div class="diagram-title">رسم المركبة / Vehicle Diagram</div>
                <div class="diagram-image">
                  <img src="${diagramUri}" alt="Vehicle Diagram" />
                  ${damageMarkersSvg}
                </div>
                <div class="diagram-meta">
                  Damages marked: ${damageMarkers.length} | التأريخ: ${deliveryDate}
                </div>
                
                <!-- Notes Below Diagram -->
                <div class="notes-card" style="margin-top: 16px;">
                  <div class="notes-title">ملاحظات عامة عن حالة المركبة:<br/>General Notes on Vehicle Condition:</div>
                  <div class="notes-body">${deliveryNotes}</div>
                </div>
                
                <!-- Damage List with Coordinates -->
                ${damageMarkers.length > 0 ? `
                <div style="margin-top: 12px;">
                  <div style="text-align: center; font-weight: 600; font-size: 11px; color: #1f2937; margin-bottom: 8px;">قائمة الأضرار / Damage List</div>
                  <div class="damage-list">${damageList}</div>
                </div>
                ` : ''}
              </div>
              
              <!-- RIGHT COLUMN: All Delivery Data + Conditions -->
              <div class="delivery-data-column">
                <!-- Row 1: Date & Time -->
                <div class="data-row">
                  <div class="data-field">
                    <div class="field-label">تاريخ التسليم / Delivery Date</div>
                    <div class="field-value">${deliveryDate}</div>
                  </div>
                  <div class="data-field">
                    <div class="field-label">وقت التسليم / Delivery Time</div>
                    <div class="field-value">${deliveryTime}</div>
                  </div>
                </div>
                
                <!-- Row 2: Fuel & Vehicle Number -->
                <div class="data-row">
                  <div class="data-field">
                    <div class="field-label">كمية الوقود / Fuel Quantity</div>
                    <div class="field-value">${fuelQuantity}</div>
                  </div>
                  <div class="data-field">
                    <div class="field-label">رقم المركبة / Vehicle Number</div>
                    <div class="field-value">${vehicleNumber}</div>
                  </div>
                </div>
                
                <!-- Row 3: Make/Model & Mileage -->
                <div class="data-row">
                  <div class="data-field">
                    <div class="field-label">الماركة والموديل والسنة / Make and Model and Year</div>
                    <div class="field-value">${makeModelYear}</div>
                  </div>
                  <div class="data-field">
                    <div class="field-label">قراءة عداد المسافة (كم) / Mileage Reading (km)</div>
                    <div class="field-value">${mileage}</div>
                  </div>
                </div>
                
                <!-- Conditions Section -->
                <div class="conditions-inline">
                  <!-- Condition Row 1 -->
                  <div class="condition-row-inline">
                    <div class="condition-inline">
                      <div class="condition-label-inline">النظافة الخارجية / External Cleanliness:</div>
                      <div class="condition-value-inline">${conditionValue(deliveryForm?.cleanliness_external)}</div>
                    </div>
                    <div class="condition-inline">
                      <div class="condition-label-inline">النظافة الداخلية / Internal Cleanliness:</div>
                      <div class="condition-value-inline">${conditionValue(deliveryForm?.cleanliness_internal)}</div>
                    </div>
                  </div>
                  
                  <!-- Condition Row 2 -->
                  <div class="condition-row-inline">
                    <div class="condition-inline">
                      <div class="condition-label-inline">الإطارات / Tires:</div>
                      <div class="condition-value-inline">${conditionValue(deliveryForm?.tire_condition)}</div>
                    </div>
                    <div class="condition-inline">
                      <div class="condition-label-inline">الأضواء والإشارات / Lights & Signals:</div>
                      <div class="condition-value-inline">${conditionValue(deliveryForm?.lights_condition)}</div>
                    </div>
                  </div>
                  
                  <!-- Condition Row 3 -->
                  <div class="condition-row-inline">
                    <div class="condition-inline">
                      <div class="condition-label-inline">الرافعة / Jack:</div>
                      <div class="condition-value-inline">${conditionValue(deliveryForm?.jack_condition)}</div>
                    </div>
                    <div class="condition-inline">
                      <div class="condition-label-inline">طفاية الحريق / Fire Extinguisher:</div>
                      <div class="condition-value-inline">${conditionValue(deliveryForm?.fire_extinguisher_condition)}</div>
                    </div>
                  </div>
                  
                  <!-- Condition Row 4 -->
                  <div class="condition-row-inline">
                    <div class="condition-inline">
                      <div class="condition-label-inline">الإطار الاحتياطي / Spare Tire:</div>
                      <div class="condition-value-inline">${conditionValue(deliveryForm?.spare_tire_condition)}</div>
                    </div>
                    <div class="condition-inline">
                      <div class="condition-label-inline">الأدوات / Tools:</div>
                      <div class="condition-value-inline">${conditionValue(deliveryForm?.tools_condition)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="section-title">التوقيعات / Signatures</div>
            <div class="signature-grid">
              <div class="signature-card">
                <div class="signature-heading">${isPartnership ? 'توقيع السائق الأول / Driver A Signature' : 'توقيع السائق / Driver Signature'}</div>
                <div class="signature-body">
                  ${driverSignature ? `<img src="${driverSignature}" alt="Driver Signature" class="signature-image" />` : '<div class="signature-placeholder">لا يوجد توقيع / No signature</div>'}
                </div>
              </div>
              ${isPartnership && secondaryDriverSignature ? `
              <div class="signature-card">
                <div class="signature-heading">توقيع السائق الثاني / Driver B Signature</div>
                <div class="signature-body">
                  <img src="${secondaryDriverSignature}" alt="Secondary Driver Signature" class="signature-image" />
                </div>
              </div>
              ` : ''}
              <div class="signature-card">
                <div class="signature-heading">توقيع الشركة / Company Signature</div>
                <div class="signature-body">
                  ${adminSignature ? `<img src="${adminSignature}" alt="Company Signature" class="signature-image" />` : '<div class="signature-placeholder">لا يوجد توقيع / No signature</div>'}
                </div>
              </div>
            </div>
          </div>

          <div class="footer-note">
            Form Generated: ${new Date().toLocaleString()} | Sharjah Transport Solutions Company © 2025
          </div>
        </div>
      </body>
    </html>`;
};

export const DeliveryHistoryDisplay: React.FC<DeliveryHistoryDisplayProps> = ({
  assignmentId,
  vehicle,
  driver,
  suffix,
}) => {
  // ...existing hooks and logic...
  // ...existing code...
  const [loading, setLoading] = useState(true);
  const [deliveryForm, setDeliveryForm] = useState<any>(null);
  const [damageMarkers, setDamageMarkers] = useState<DamageMarker[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isPartnership, setIsPartnership] = useState(false);
  // Local state for vehicle and driver (fetched if not provided as props)
  const [localVehicle, setLocalVehicle] = useState<any>(null);
  const [localDriver, setLocalDriver] = useState<any>(null);
  const [pdfGenerating, setPdfGenerating] = useState<boolean>(false);

  useEffect(() => {
    loadDeliveryData();
  }, [assignmentId, suffix]);

  const loadDeliveryData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🚚 Loading delivery data for assignment ID:', assignmentId, suffix ? `(Suffix: ${suffix})` : '');

      // ✅ NEW: Check if this is a partnership assignment
      const { getAssignmentHistory } = await import('../../api/supabase');
      const allAssignments = await getAssignmentHistory();
      const assignment = allAssignments.find((a: any) => a.id === assignmentId);
      const isPartnershipAssignment = !!assignment?.secondary_driver_id;
      setIsPartnership(isPartnershipAssignment);
      
      console.log('🔍 Delivery - Assignment check:', {
        assignmentId,
        hasSecondaryDriver: isPartnershipAssignment,
        secondaryDriverId: assignment?.secondary_driver_id,
        suffix
      });

      // Load delivery form using hybrid method (checks both database and local storage)
      // Pass suffix to filter partnership forms (A or B)
      const formData = await getVehicleDeliveryFormByAssignmentHybrid(assignmentId, suffix);
      console.log('🚚 Delivery form data:', formData);
      console.log('🚚 Form data fields available:', formData ? Object.keys(formData) : 'No form data');
      
      if (formData) {
        console.log('🚚 Delivery date/time fields:', {
          delivery_date: formData.delivery_date,
          delivery_time: formData.delivery_time,
          assignment_date: formData.assignment_date,
          assignment_time: formData.assignment_time,
          created_at: formData.created_at
        });
      }
      
      setDeliveryForm(formData);

      // Fetch vehicle data if not provided as prop
      if (!vehicle && formData?.vehicle_id) {
        console.log(`Fetching vehicle with ID: ${formData.vehicle_id}`);
        const { getVehicleById } = await import('../../api/supabase');
        const vehicleData = await getVehicleById(formData.vehicle_id);
        if (vehicleData) {
          console.log('✅ Fetched vehicle data:', vehicleData);
          setLocalVehicle(vehicleData);
        }
      } else if (vehicle) {
        setLocalVehicle(vehicle);
      }

      // Fetch driver data if not provided as prop
      if (!driver && formData?.driver_id) {
        console.log(`Fetching driver with ID: ${formData.driver_id}`);
        const { getDriverById } = await import('../../api/supabase');
        const driverData = await getDriverById(formData.driver_id);
        if (driverData) {
          console.log('✅ Fetched driver data:', driverData);
          setLocalDriver(driverData);
        }
      } else if (driver) {
        setLocalDriver(driver);
      }

      // Load damage markers if form exists
      if (formData?.id) {
        console.log('🔍 Attempting to load damages for delivery form ID:', formData.id);
        const damages = await getVehicleDamagesByDeliveryFormHybrid(formData.id);
        console.log('✅ Loaded delivery damage markers:', damages);
        setDamageMarkers(damages || []);
        
        if (!damages || damages.length === 0) {
          console.log('⚠️ No damages found for delivery form. This might be expected if no damages were marked during delivery.');
        }
      } else {
        console.log('⚠️ No delivery form found for assignment ID:', assignmentId);
      }
    } catch (err: any) {
      console.error('❌ Error loading delivery data:', err);
      setError(err.message || 'Failed to load delivery form data');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'Not set';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return 'Invalid date';
    }
  };

  const handlePrint = () => {
    if (!isWeb) {
      Alert.alert('Print', 'Printing is only available on the web version of the app.');
      return;
    }

    if (!deliveryForm) {
      Alert.alert('Print', 'Form data is still loading. Please try again in a moment.');
      return;
    }

    // Debug: Log all available data before printing
    console.log('📋 PRINT DEBUG - Available data:', {
      deliveryForm: deliveryForm ? Object.keys(deliveryForm) : 'null',
      vehicle: localVehicle || vehicle ? { licensePlate: (localVehicle || vehicle)?.licensePlate, makeModel: (localVehicle || vehicle)?.makeModel, year: (localVehicle || vehicle)?.year } : 'null',
      driver: localDriver || driver ? { name: (localDriver || driver)?.name, phone: (localDriver || driver)?.phone } : 'null',
      damageMarkers: damageMarkers.length,
      isPartnership
    });

    const printHtml = buildDeliveryPrintHtml({
      deliveryForm,
      vehicle: localVehicle || vehicle,
      driver: localDriver || driver,
      damageMarkers,
      isPartnership,
      assetUris: {
        logo: resolveAssetUri(LOGO_ASSET),
        vehicleDiagram: resolveAssetUri(VEHICLE_DIAGRAM_ASSET),
      },
    });

    const printWindow = window.open('', '_blank', 'width=900,height=1200,scrollbars=yes');
    if (!printWindow) {
      Alert.alert('Error', 'Popup blocked — please allow popups for this site to print the form');
      return;
    }

    printWindow.document.open();
    printWindow.document.write(printHtml);
    printWindow.document.close();
  };

  const handleGeneratePDF = async (saveToStorage: boolean = false) => {
    if (!isWeb) {
      Alert.alert('PDF Generation', 'PDF generation is only available on the web version of the app.');
      return;
    }

    if (!deliveryForm) {
      Alert.alert('PDF Generation', 'Form data is still loading. Please try again in a moment.');
      return;
    }

    try {
      setPdfGenerating(true);
      console.log('📄 Generating PDF using Gotenberg...');

      // Convert assets to base64 for embedding in PDF
      console.log('🔄 Converting assets to base64...');
      const logoUrl = resolveAssetUri(LOGO_ASSET);
      const diagramUrl = resolveAssetUri(VEHICLE_DIAGRAM_ASSET);
      
      const [logoBase64, diagramBase64] = await Promise.all([
        imageUrlToBase64(logoUrl),
        imageUrlToBase64(diagramUrl),
      ]);

      console.log('✅ Assets converted:', {
        logo: logoBase64 ? 'Success' : 'Failed',
        diagram: diagramBase64 ? 'Success' : 'Failed',
      });

      // Build HTML with base64-encoded assets
      const printHtml = buildDeliveryPrintHtml({
        deliveryForm,
        vehicle: localVehicle || vehicle,
        driver: localDriver || driver,
        damageMarkers,
        isPartnership,
        assetUris: {
          logo: logoBase64 || logoUrl,
          vehicleDiagram: diagramBase64 || diagramUrl,
        },
      });

      const fileName = `Delivery-Report-${deliveryForm?.reference_number || assignmentId}-${Date.now()}`;

      if (saveToStorage) {
        // Generate and store in Supabase
        const result = await generateAndStorePDF({
          html: printHtml,
          fileName,
          storageBucket: 'delivery-forms',
          storagePath: 'pdfs',
        });

        if (result.success && result.pdfUrl) {
          Alert.alert(
            '✅ PDF Generated',
            `PDF has been saved to cloud storage and is available at:\n${result.pdfUrl}`,
            [
              { text: 'OK' },
              { text: 'Open PDF', onPress: () => window.open(result.pdfUrl, '_blank') }
            ]
          );
        } else {
          throw new Error(result.error || 'Failed to save PDF');
        }
      } else {
        // Generate and download directly
        await generateAndDownloadPDF(printHtml, fileName);
        Alert.alert('PDF Downloaded', 'The PDF has been downloaded to your device.');
      }
    } catch (error) {
      console.error('❌ PDF generation failed:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      // Check if it's a network/connection error
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError') || errorMessage.includes('fetch')) {
        Alert.alert(
          'PDF Service Unavailable',
          'The PDF generation service is not running.\n\nTo fix this:\n\n1. Make sure Docker Desktop is running\n2. Open terminal and run: docker-compose up -d\n3. Wait a moment and try again\n\nOr use the "Print Form" button instead.',
          [{ text: 'OK' }]
        );
      } else {
        Alert.alert(
          'PDF Generation Failed',
          errorMessage,
          [{ text: 'OK' }]
        );
      }
    } finally {
      setPdfGenerating(false);
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        <Text style={styles.loadingText}>Loading delivery form...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.errorContainer}>
        <Text style={styles.errorText}>{error}</Text>
      </View>
    );
  }

  if (!deliveryForm) {
    return (
      <View style={styles.noDataContainer}>
        <Text style={styles.noDataText}>No delivery form found for this assignment</Text>
      </View>
    );
  }

  const getMarkerColor = (severity: string) => {
    switch (severity) {
      case 'major': return '#ff4444';
      case 'moderate': return '#ffaa00';
      case 'minor': return '#44ff44';
      default: return '#44ff44';
    }
  };

  const renderVehicleDiagram = () => {
    console.log('🚚 Rendering delivery vehicle diagram with damage markers:', damageMarkers);
    
    const getMarkerColor = (severity: string) => {
      switch (severity) {
        case 'major': return '#ff4444';
        case 'moderate': return '#ffaa00';
        case 'minor': return '#44ff44';
        default: return '#44ff44';
      }
    };

    return (
      <View style={styles.diagramContainer}>
        <Text style={styles.diagramTitle}>
          مخطط المركبة والأضرار / Vehicle Diagram & Damage
        </Text>
        
        <View style={styles.vehicleDiagram}>
          <View style={styles.imageContainer}>
            <TouchableOpacity style={styles.touchableImage} disabled={true}>
              <Image
                source={VEHICLE_DIAGRAM_ASSET}
                style={styles.teslaImage}
                resizeMode="contain"
                onError={(error) => {
                  console.error('Failed to load vehicle diagram image:', error.nativeEvent.error);
                }}
              />
              <Svg style={styles.svgOverlay} viewBox="0 0 350 250">
                {damageMarkers.map((marker, index) => (
                  <React.Fragment key={marker.id}>
                    <Circle
                      cx={marker.x}
                      cy={marker.y}
                      r="8"
                      fill={getMarkerColor(marker.severity)}
                      stroke="#000"
                      strokeWidth="2"
                    />
                    <SvgText
                      x={marker.x}
                      y={marker.y + 2}
                      fontSize="10"
                      fill="#ffffff"
                      textAnchor="middle"
                      fontWeight="bold"
                    >
                      {index + 1}
                    </SvgText>
                  </React.Fragment>
                ))}
              </Svg>
            </TouchableOpacity>
          </View>

          {/* Instructions */}
          <Text style={styles.instructionText}>
            عرض الأضرار المسجلة / Displaying Recorded Damage
          </Text>
        </View>

        {/* Damage List */}
        {damageMarkers.length > 0 && (
          <View style={styles.damageList}>
            <Text style={styles.damageListTitle}>قائمة الأضرار / Damage List:</Text>
            {damageMarkers.map((marker, index) => (
              <View key={marker.id} style={styles.damageItem}>
                <View style={styles.damageItemHeader}>
                  <View style={[styles.damageNumber, { backgroundColor: getMarkerColor(marker.severity) }]}>
                    <Text style={styles.damageNumberText}>{index + 1}</Text>
                  </View>
                  <View style={styles.damageInfo}>
                    <Text style={styles.damageItemText}>
                      {marker.type} - {marker.severity}
                    </Text>
                    {marker.description && (
                      <Text style={styles.damageDescription}>
                        {marker.description}
                      </Text>
                    )}
                    {marker.photoUrl && (
                      <Image
                        source={{ uri: marker.photoUrl }}
                        style={styles.damagePhoto}
                        resizeMode="cover"
                      />
                    )}
                  </View>
                </View>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  };

  // Defensive fuel display logic for JSX (must be after deliveryForm is set and outside JSX)
  const fuelDisplay =
    typeof deliveryForm?.fuel_level === 'number'
      ? `${deliveryForm.fuel_level}%`
      : deliveryForm?.fuel_quantity
      ? String(deliveryForm.fuel_quantity)
      : 'N/A';

  // Defensive make/model/year display logic
  const makeModelYearDisplay =
    vehicle?.makeModel && vehicle?.year
      ? `${vehicle.makeModel} (${vehicle.year})`
      : deliveryForm?.make_model_year
      ? deliveryForm.make_model_year
      : 'N/A';

  return (
    <>
      {/* Main Display */}
      <View style={styles.container} nativeID="delivery-history-container">
        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        {/* Header with Logo */}
        <View style={styles.headerSection}>
          <View style={styles.headerContent}>
            <View style={styles.headerLeft}>
              <View style={styles.logoContainer}>
                <Image
                  source={require('../../../assets/logo-icon-only.svg')}
                  style={styles.logoImage}
                  resizeMode="contain"
                />
              </View>
              <View style={styles.companyInfo}>
                <Text style={styles.companyNameAr}>الشارقة لحلول النقل</Text>
                <Text style={styles.companyNameEn}>Sharjah Transport Solutions</Text>
              </View>
            </View>
            <View style={styles.headerCenter}>
              <Text style={styles.typeAr}>تسليم المركبة</Text>
              <Text style={styles.typeEn}>Vehicle Delivery</Text>
            </View>
            <View style={styles.headerRight}>
            </View>
          </View>
        </View>

        {/* Basic Information */}
        <Card style={styles.section}>
          <View style={styles.basicInfoGrid}>
            <View style={styles.inputGroup}>
              <Text style={styles.labelText}>اسم الشركة / Company Name</Text>
              <View style={styles.readOnlyInput}>
                <Text style={styles.readOnlyText}>الشارقة لحلول النقل / Sharjah Transport Solutions</Text>
              </View>
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.labelText}>رقم اللوحة / License Plate</Text>
              <View style={styles.readOnlyInput}>
                <Text style={styles.readOnlyText}>
                  {vehicle?.licensePlate || deliveryForm?.vehicle_license_plate || deliveryForm?.license_plate || 'Not available'}
                </Text>
              </View>
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.labelText}>اسم السائق / Driver Name</Text>
              <View style={styles.readOnlyInput}>
                <Text style={styles.readOnlyText}>
                  {driver?.name || deliveryForm?.driver_name || deliveryForm?.primary_driver_name || 'Not available'}
                </Text>
              </View>
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.labelText}>رقم الهاتف / Phone Number</Text>
              <View style={styles.readOnlyInput}>
                <Text style={styles.readOnlyText}>
                  {driver?.phone || deliveryForm?.driver_phone || deliveryForm?.primary_driver_phone || 'Not available'}
                </Text>
              </View>
            </View>
          </View>
        </Card>

        {/* Vehicle Delivery Data */}
        <Card style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>
              بيانات تسليم المركبة / Vehicle Delivery Data
            </Text>
          </View>
          
          <View style={styles.deliveryContent}>
            <View style={styles.leftColumn}>
              {renderVehicleDiagram()}
              <View style={styles.notesContainer}>
                <Text style={styles.notesLabel}>
                  ملاحظات عامة عن حالة المركبة:{'\n'}
                  General Notes on Vehicle Condition:
                </Text>
                <View style={styles.notesDisplay}>
                  <Text style={styles.notesText}>
                    {deliveryForm?.delivery_notes || deliveryForm?.additional_notes || deliveryForm?.notes || 'لا توجد ملاحظات / No notes'}
                  </Text>
                </View>
              </View>
            </View>
            
            <View style={styles.rightColumn}>
              <View style={styles.dateTimeRow}>
                <View style={styles.inputGroup}>
                  <Text style={styles.labelText}>تاريخ التسليم / Delivery Date</Text>
                  <View style={styles.readOnlyInput}>
                    <Text style={styles.readOnlyText}>{formatDate(deliveryForm.delivery_date)}</Text>
                  </View>
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.labelText}>وقت التسليم / Delivery Time</Text>
                  <View style={styles.readOnlyInput}>
                    <Text style={styles.readOnlyText}>{deliveryForm.delivery_time || 'N/A'}</Text>
                  </View>
                </View>
              </View>

              <View style={styles.dateTimeRow}>
                <View style={styles.inputGroup}>
                  <Text style={styles.labelText}>كمية الوقود / Fuel Quantity</Text>
                  <View style={styles.readOnlyInput}>
                    {/* Move logic to variable for clarity and to avoid JSX parse errors */}
                    <Text style={styles.readOnlyText}>{fuelDisplay}</Text>
                  </View>
                </View>
                  {/* Defensive fuel display logic for JSX is handled above the return statement */}
                <View style={styles.inputGroup}>
                  <Text style={styles.labelText}>رقم المركبة / Vehicle Number</Text>
                  <View style={styles.readOnlyInput}>
                    <Text style={styles.readOnlyText}>{vehicle?.licensePlate || 'N/A'}</Text>
                  </View>
                </View>
              </View>

              <View style={styles.dateTimeRow}>
                <View style={styles.inputGroup}>
                  <Text style={styles.labelText}>الماركة والموديل والسنة / Make and Model and Year</Text>
                  <View style={styles.readOnlyInput}>
                    <Text style={styles.readOnlyText}>{makeModelYearDisplay}</Text>
                  </View>
                </View>
                <View style={styles.inputGroup}>
                  <Text style={styles.labelText}>قراءة عداد المسافة (كم) / Mileage Reading (km)</Text>
                  <View style={styles.readOnlyInput}>
                    <Text style={styles.readOnlyText}>{deliveryForm.delivery_mileage || deliveryForm.final_mileage || 'N/A'}</Text>
                  </View>
                </View>
              </View>
              
              <View style={styles.conditionsContainer}>
                <View style={styles.conditionRow}>
                  <View style={styles.conditionItem}>
                    <Text style={styles.conditionLabel}>النظافة الخارجية / External Cleanliness:</Text>
                    <View style={styles.conditionDisplay}>
                      <Text style={styles.conditionDisplayText}>{deliveryForm.cleanliness_external || 'جيد / Good'}</Text>
                    </View>
                  </View>
                  <View style={styles.conditionItem}>
                    <Text style={styles.conditionLabel}>النظافة الداخلية / Internal Cleanliness:</Text>
                    <View style={styles.conditionDisplay}>
                      <Text style={styles.conditionDisplayText}>{deliveryForm.cleanliness_internal || 'جيد / Good'}</Text>
                    </View>
                  </View>
                </View>
                <View style={styles.conditionRow}>
                  <View style={styles.conditionItem}>
                    <Text style={styles.conditionLabel}>الإطارات / Tires:</Text>
                    <View style={styles.conditionDisplay}>
                      <Text style={styles.conditionDisplayText}>{deliveryForm.tire_condition || 'جيد / Good'}</Text>
                    </View>
                  </View>
                  <View style={styles.conditionItem}>
                    <Text style={styles.conditionLabel}>الأضواء والإشارات / Lights & Signals:</Text>
                    <View style={styles.conditionDisplay}>
                      <Text style={styles.conditionDisplayText}>{deliveryForm.lights_condition || 'جيد / Good'}</Text>
                    </View>
                  </View>
                </View>
                <View style={styles.conditionRow}>
                  <View style={styles.conditionItem}>
                    <Text style={styles.conditionLabel}>الرافعة / Jack:</Text>
                    <View style={styles.conditionDisplay}>
                      <Text style={styles.conditionDisplayText}>{deliveryForm.jack_condition || 'جيد / Good'}</Text>
                    </View>
                  </View>
                  <View style={styles.conditionItem}>
                    <Text style={styles.conditionLabel}>طفاية الحريق / Fire Extinguisher:</Text>
                    <View style={styles.conditionDisplay}>
                      <Text style={styles.conditionDisplayText}>{deliveryForm.fire_extinguisher_condition || 'جيد / Good'}</Text>
                    </View>
                  </View>
                </View>
                <View style={styles.conditionRow}>
                  <View style={styles.conditionItem}>
                    <Text style={styles.conditionLabel}>الإطار الاحتياطي / Spare Tire:</Text>
                    <View style={styles.conditionDisplay}>
                      <Text style={styles.conditionDisplayText}>{deliveryForm.spare_tire_condition || 'جيد / Good'}</Text>
                    </View>
                  </View>
                  <View style={styles.conditionItem}>
                    <Text style={styles.conditionLabel}>الأدوات / Tools:</Text>
                    <View style={styles.conditionDisplay}>
                      <Text style={styles.conditionDisplayText}>{deliveryForm.tools_condition || 'جيد / Good'}</Text>
                    </View>
                  </View>
                </View>
              </View>
            </View>
          </View>
        </Card>

        {/* Signatures */}
        <Card style={styles.section}>
          <View style={styles.signatureSection}>
            <View style={styles.signatureRow}>
              <Card style={styles.signatureCard}>
                <View style={styles.signatureHeader}>
                  <Text style={styles.signatureTitle}>
                    {isPartnership ? 'توقيع السائق الأول / Driver A Signature' : 'توقيع السائق / Driver Signature'}
                  </Text>
                </View>
                <View style={styles.signatureContent}>
                  {deliveryForm.driver_signature || deliveryForm.primary_driver_signature ? (
                    <SignatureDisplay 
                      signature={deliveryForm.driver_signature || deliveryForm.primary_driver_signature}
                      onEdit={() => {}}
                      readOnly={true}
                    />
                  ) : (
                    <View style={styles.signatureBox}>
                      <Text style={styles.signaturePlaceholder}>لا يوجد توقيع / No signature</Text>
                    </View>
                  )}
                </View>
              </Card>

              {/* ✅ NEW: Secondary Driver Signature for Partnerships */}
              {isPartnership && deliveryForm.secondary_driver_signature && (
                <Card style={styles.signatureCard}>
                  <View style={styles.signatureHeader}>
                    <Text style={styles.signatureTitle}>توقيع السائق الثاني / Driver B Signature</Text>
                  </View>
                  <View style={styles.signatureContent}>
                    <SignatureDisplay 
                      signature={deliveryForm.secondary_driver_signature}
                      onEdit={() => {}}
                      readOnly={true}
                    />
                  </View>
                </Card>
              )}

              <Card style={styles.signatureCard}>
                <View style={styles.signatureHeader}>
                  <Text style={styles.signatureTitle}>توقيع الشركة / Company Signature</Text>
                </View>
                <View style={styles.signatureContent}>
                  {deliveryForm.company_signature ? (
                    <SignatureDisplay 
                      signature={deliveryForm.company_signature}
                      onEdit={() => {}}
                      readOnly={true}
                    />
                  ) : (
                    <View style={styles.signatureBox}>
                      <Text style={styles.signaturePlaceholder}>لا يوجد توقيع / No signature</Text>
                      <Text style={styles.instructionText}>
                        Available signature fields: {Object.keys(deliveryForm).filter(key => key.includes('signature')).join(', ') || 'none'}
                      </Text>
                    </View>
                  )}
                </View>
              </Card>
            </View>
          </View>
        </Card>

        {/* Print & PDF Buttons */}
        {Platform.OS === 'web' && (
          <View style={{ margin: 16, alignItems: 'center', gap: 12 }}>
            <Button
              title="طباعة النموذج / Print Form"
              onPress={handlePrint}
              variant="primary"
              style={{ minWidth: 200, paddingHorizontal: 24, paddingVertical: 12 }}
              disabled={loading || !deliveryForm}
            />
            
            {/* PDF Generation Buttons (Option B - Gotenberg) */}
            <View style={{ flexDirection: 'row', gap: 12, marginTop: 8 }}>
              <Button
                title="📄 Download PDF"
                onPress={() => handleGeneratePDF(false)}
                variant="secondary"
                style={{ paddingHorizontal: 20, paddingVertical: 10 }}
                disabled={loading || !deliveryForm || pdfGenerating}
              />
              <Button
                title="☁️ Save PDF to Cloud"
                onPress={() => handleGeneratePDF(true)}
                variant="secondary"
                style={{ paddingHorizontal: 20, paddingVertical: 10 }}
                disabled={loading || !deliveryForm || pdfGenerating}
              />
            </View>
            
            {/* Show status if PDF is generating */}
            {pdfGenerating && (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 8 }}>
                <ActivityIndicator size="small" color={theme.colors.primary} />
                <Text style={{ fontSize: 14, color: theme.colors.gray600 }}>
                  Generating PDF...
                </Text>
              </View>
            )}
          </View>
        )}
      </ScrollView>
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.gray50,
  },
  scrollView: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background,
  },
  loadingText: {
    marginTop: theme.spacing.md,
    fontSize: 16,
    color: theme.colors.textSecondary,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background,
    padding: theme.spacing.lg,
  },
  errorText: {
    fontSize: 16,
    color: theme.colors.error,
    textAlign: 'center',
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.background,
    padding: theme.spacing.lg,
  },
  noDataText: {
    fontSize: 16,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
  headerSection: {
    backgroundColor: theme.colors.white,
    borderWidth: 2,
    borderColor: theme.colors.gray400,
    margin: theme.spacing.md,
    marginBottom: theme.spacing.sm,
  },
  headerContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing.lg,
  },
  headerLeft: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
  },
  logoContainer: {
    width: 60,
    height: 60,
    backgroundColor: theme.colors.white,
    borderRadius: 30,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: theme.spacing.md,
    borderWidth: 2,
    borderColor: theme.colors.primary,
    shadowColor: theme.colors.gray900,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 4,
  },
  logoImage: {
    width: 40,
    height: 40,
  },
  companyInfo: {
    flex: 1,
  },
  companyNameAr: {
    fontSize: 16,
    fontWeight: 'bold' as any,
    color: theme.colors.textPrimary,
    textAlign: 'left',
  },
  companyNameEn: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    textAlign: 'left',
    marginTop: 2,
  },
  headerCenter: {
    flex: 1,
    alignItems: 'center',
  },
  headerRight: {
    flex: 1,
    alignItems: 'flex-end',
  },
  typeAr: {
    fontSize: 18,
    fontWeight: 'bold' as any,
    color: theme.colors.primary,
    textAlign: 'center',
  },
  typeEn: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginTop: 4,
  },
  section: {
    margin: theme.spacing.md,
    marginBottom: theme.spacing.sm,
  },
  basicInfoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing.md,
  },
  inputGroup: {
    flex: 1,
    minWidth: 200,
  },
  labelText: {
    fontSize: 14,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.xs,
    textAlign: 'right',
  },
  readOnlyInput: {
    borderWidth: 1,
    borderColor: theme.colors.gray300,
    borderRadius: 8,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.gray100,
    minHeight: 44,
    justifyContent: 'center',
  },
  readOnlyText: {
    fontSize: 14,
    color: theme.colors.textPrimary,
    textAlign: 'right',
  },
  sectionHeader: {
    backgroundColor: theme.colors.gray100,
    padding: theme.spacing.md,
    alignItems: 'center',
    marginBottom: theme.spacing.md,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold' as any,
    color: theme.colors.textPrimary,
    textAlign: 'center',
  },
  deliveryContent: {
    flexDirection: 'row',
    gap: theme.spacing.lg,
  },
  leftColumn: {
    flex: 1,
  },
  rightColumn: {
    flex: 1,
  },
  diagramContainer: {
    borderWidth: 1,
    borderColor: theme.colors.gray300,
    backgroundColor: theme.colors.gray50,
    padding: theme.spacing.sm,
    borderRadius: 8,
    marginBottom: theme.spacing.sm,
  },
  diagramTitle: {
    fontSize: 14,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    textAlign: 'center',
    marginBottom: theme.spacing.sm,
  },
  vehicleDiagram: {
    alignItems: 'center',
  },
  imageContainer: {
    position: 'relative',
    alignItems: 'center',
  },
  touchableImage: {
    position: 'relative',
  },
  teslaImage: {
    width: 350,
    height: 250,
    marginVertical: theme.spacing.xs,
  },
  svgOverlay: {
    position: 'absolute',
    top: theme.spacing.xs,
    left: 0,
    width: 350,
    height: 250,
    zIndex: 1,
  },
  instructionText: {
    fontSize: 11,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginTop: theme.spacing.sm,
    fontStyle: 'italic' as any,
  },
  damageList: {
    marginTop: theme.spacing.md,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.white,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.gray300,
  },
  damageListTitle: {
    fontSize: 14,
    fontWeight: '600' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.sm,
    textAlign: 'center',
  },
  damageItem: {
    marginBottom: theme.spacing.xs,
  },
  damageItemHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  damageNumber: {
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  damageNumberText: {
    color: theme.colors.white,
    fontSize: 12,
    fontWeight: 'bold' as any,
  },
  damageItemText: {
    fontSize: 12,
    color: theme.colors.textPrimary,
    fontWeight: '500' as any,
  },
  damageInfo: {
    flex: 1,
    marginHorizontal: theme.spacing.sm,
  },
  damageDescription: {
    fontSize: 11,
    color: theme.colors.textSecondary,
    marginTop: 2,
    fontStyle: 'italic' as any,
  },
  damagePhoto: {
    width: 120,
    height: 120,
    marginTop: theme.spacing.sm,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: theme.colors.gray300,
  },
  notesContainer: {
    marginTop: theme.spacing.md,
  },
  notesLabel: {
    fontSize: 14,
    fontWeight: '500' as any,
    color: theme.colors.textPrimary,
    marginBottom: theme.spacing.sm,
    textAlign: 'center',
  },
  notesDisplay: {
    borderWidth: 1,
    borderColor: theme.colors.gray300,
    borderRadius: 8,
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.gray100,
    minHeight: 80,
  },
  notesText: {
    fontSize: 14,
    color: theme.colors.textPrimary,
    textAlign: 'right',
    lineHeight: 20,
  },
  dateTimeRow: {
    flexDirection: 'row',
    gap: theme.spacing.md,
    marginBottom: theme.spacing.md,
  },
  conditionsContainer: {
    gap: theme.spacing.sm,
  },
  conditionRow: {
    flexDirection: 'row',
    gap: theme.spacing.md,
    marginBottom: theme.spacing.sm,
  },
  conditionItem: {
    flex: 1,
  },
  conditionLabel: {
    fontSize: 12,
    color: theme.colors.textPrimary,
    textAlign: 'right',
    marginBottom: theme.spacing.xs,
    fontWeight: '500' as any,
  },
  conditionDisplay: {
    paddingHorizontal: theme.spacing.sm,
    paddingVertical: theme.spacing.xs,
    borderWidth: 1,
    borderColor: theme.colors.gray300,
    borderRadius: 4,
    backgroundColor: theme.colors.gray100,
    justifyContent: 'center',
    alignItems: 'center',
  },
  conditionDisplayText: {
    fontSize: 10,
    color: theme.colors.textPrimary,
    textAlign: 'center',
  },
  signatureSection: {
    margin: theme.spacing.md,
  },
  signatureRow: {
    flexDirection: 'row',
    gap: theme.spacing.md,
  },
  signatureCard: {
    flex: 1,
  },
  signatureHeader: {
    backgroundColor: theme.colors.primary,
    padding: theme.spacing.md,
    borderRadius: 8,
    marginBottom: theme.spacing.md,
  },
  signatureTitle: {
    fontSize: 14,
    fontWeight: '600' as any,
    color: theme.colors.white,
    textAlign: 'center',
  },
  signatureContent: {
    gap: theme.spacing.md,
  },
  signatureBox: {
    height: 60,
    borderWidth: 2,
    borderColor: theme.colors.gray300,
    borderRadius: 8,
    backgroundColor: theme.colors.gray50,
    justifyContent: 'center',
    alignItems: 'center',
  },
  signaturePlaceholder: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    textAlign: 'center',
  },
});

